import type { SetupNeed } from "@shared/connectors/setupNeed";
import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyDataGrid,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type ReactElement,
  type RefObject,
} from "react";
import { Link, useSearchParams } from "wouter";
import type { DataflowSummary } from "@shared/types/dataflows";
import {
  RUN_ID_REGEX,
  type RunConnectionStep,
  type RunSummaryRow,
  type RunTotals,
} from "@shared/types/runs";
import { importOffReason as importOffReasonOf } from "@/components/connections/LastRunPanel";
import { destinationBlockReason, useDestinationReadiness } from "@/hooks/use-destination-readiness";
import { RunTablesGrid } from "@/components/connections/RunTablesGrid";
import { CursorPager, NewerRowsNotice } from "@/components/paging/CursorPager";
import { StartedByCell, StartedCell } from "@/components/runs/RunsTable";
import { isNotStarted, NOT_STARTED_LABEL } from "@/components/runs/runFormatting";
import { StatusCell } from "@/components/runs/StatusCell";
import {
  STARTED_BY_KIND_LABELS,
  STEP_LABELS,
  extractUnreported,
  formatCount,
  formatDuration,
  isTerminalRun,
  loadHadNoDestination,
  loadsSendingFiles,
  loadsWaitingForSlot,
  ownRunStatus,
  runStatusPresentation,
  stepStatusPresentation,
} from "@/components/runs/runFormatting";
import { pagerRange, useCursorPager, useNextPage } from "@/hooks/use-cursor-pager";
import { usePermissions } from "@/hooks/use-permissions";
import {
  RUNS_DEFAULT_PAGE_SIZE,
  RUNS_PAGE_SIZES,
  RUN_POLL_INTERVAL_MS,
  usePollWhile,
  useRun,
  useRunsPage,
} from "@/hooks/use-runs";
import { failureMessage, type ApiClientError } from "@/lib/api";
import { pixelColumnLayout } from "@/lib/grid";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { PlayIcon, RetryIcon } from "@/lib/icons";
import { absolutePath } from "@/lib/routes";

/**
 * A connection's own run history, on its editor's Runs tab (Session 10, B17).
 *
 * One read: `GET /runs?dataflowId=` (not audited). Round 4 (4b): 20 runs a page with the Runs
 * list's `CursorPager` (Previous, Next, 10/20/50, "1-20 of N"); Next reads the page before it
 * moves, so a failure keeps the page on screen. The server adds each run's `connection` slice for
 * exactly this connection, so every number in a row is this connection's, never the whole run's:
 *
 * - Steps are this connection's own step rows (the run-level transform and finalize rows belong
 *   to the run page), each with its word, so colour is never the only signal.
 * - Duration runs from the first of those steps to start to the last to finish; a dash while any
 *   of them is still going.
 * - Tables and rows are this connection's tables only.
 *
 * Expanding a row reads that ONE run (`GET /runs/:runId`, not audited) and shows this
 * connection's tables. A row nobody expands costs nothing. The open panels sit under the grid,
 * because the library grid has no row expansion; the one just opened is scrolled into view.
 *
 * The tab is Viewer-safe: a Viewer reads everything and has no "Import now".
 */

/**
 * The page's own "Import now", when the page has one (the editor header's). The empty state then
 * starts through it, so both buttons behave the same: the import starts in place, the page
 * stays, and the button is off while an import is being asked for or is already queued or
 * running. Without it the empty state offers no start of its own.
 */
export interface ConnectionImportAction {
  onImport: () => void;
  /** The start request is in flight. */
  pending: boolean;
  /** An import of this connection is already queued or running. */
  active: boolean;
}

export interface ConnectionRunsTabProps {
  dataflow: DataflowSummary;
  importAction?: ConnectionImportAction;
  /** #2481: what the setup still needs, in the connector's words. */
  need?: SetupNeed;
}

function stepText(step: RunConnectionStep): string {
  const label = STEP_LABELS[step.step] ?? step.step;
  const attempt = step.attempt > 1 ? ` (attempt ${step.attempt})` : "";
  if (step.status === "succeeded")
    return `${label} ${formatDuration(step.durationSeconds)}${attempt}`;
  const word = stepStatusPresentation(step.status).label.toLowerCase();
  const time =
    step.status === "failed" && step.durationSeconds !== null
      ? ` after ${formatDuration(step.durationSeconds)}`
      : "";
  return `${label} ${word}${time}${attempt}`;
}

function StepsCell({ row }: { row: RunSummaryRow }) {
  const steps = row.connection?.steps ?? [];
  if (steps.length === 0) {
    return (
      <SkyText size={200} color="muted" testId={`connection-run-steps-${row.runId}`}>
        —
      </SkyText>
    );
  }
  return (
    <SkyText size={200} font="numeric" block testId={`connection-run-steps-${row.runId}`}>
      {steps.map((step, index) => (
        <span
          key={`${step.step}-${index}`}
          className={step.status === "failed" ? "text-destructive" : undefined}
        >
          {index > 0 ? " · " : ""}
          {stepText(step)}
        </span>
      ))}
    </SkyText>
  );
}

/** The extract count, or a dash when only the load reported the tables (see `rowsLineParts`). */
function rowsExtractedText(totals: RunTotals | undefined): string {
  if (!totals) return "—";
  return extractUnreported(totals) ? "—" : formatCount(totals.rowsExtracted);
}

function NumberCell({ value, testId }: { value: string; testId: string }) {
  return (
    <SkyText size={200} font="numeric" testId={testId}>
      {value}
    </SkyText>
  );
}

function panelId(runId: string): string {
  return `connection-run-tables-${runId}`;
}

/**
 * Round 3 (owner, 2026-09-30, 1jDLHQ.png): ten percentage columns gave Status 99 px at 1280 px,
 * and "Succeeded with warning" (131 px; "Partly failed" since #817) ran out of its cell into Started. The columns a value
 * sizes have pixel widths, measured in Chromium in both fonts (client/AGENTS.md), border-box with
 * the cell's 24 px padding: Status holds that pill; Started its time on two lines at the comma
 * ("Sep 29, 2026," 84 px) over "took 59m 59s" (84 px); Actions "Tables" and "View" (93 px).
 * The rest share what is left in proportion to a minimum that holds their header (the two row
 * counts on two lines, "extracted" 119 px and "loaded" 93 px).
 *
 * gridsfit (lead, 2026-09-30): the 1208 px floor scrolled at 1440 px. The duration reads under
 * the start and the trigger under the person (the Runs list's pairing), and Steps, whose text
 * wraps, keeps 112 px: the floor, 968 px, fits the bordered grid at 1280 px (971 px less its two
 * borders). Actions stays pinned for a narrower pane.
 */
const STATUS_WIDTH = 160;
const STARTED_WIDTH = 128;
const ACTIONS_WIDTH = 120;
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  startedBy: 136,
  steps: 112,
  tables: 96,
  rowsExtracted: 120,
  rowsLoaded: 96,
};

/**
 * #1201: since when THIS connection's part of a running run has been going, the first of its own
 * steps to start, so "running for" is measured on the same clock as the "took" it becomes (the
 * slice's `durationSeconds`). None of its steps started yet: null, no elapsed time. A row with no
 * slice (an older server) has only the run's start.
 */
function connectionRunningSince(row: {
  startedAt: string | null;
  connection?: { steps: ReadonlyArray<{ startedAt: string | null }> };
}): string | null {
  if (!row.connection) return row.startedAt;
  const starts = row.connection.steps
    .map((step) => step.startedAt)
    .filter((at): at is string => at !== null)
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  return starts[0] ?? null;
}

/**
 * One expanded row: this connection's tables in that run. A run still moving is re-read every
 * few seconds until it is over, as the last-run card does; a finished run is read once.
 */
function ExpandedRunTables({
  runId,
  dataflowId,
  onHide,
}: {
  runId: string;
  dataflowId: string;
  onHide: () => void;
}) {
  const run = useRun(runId);
  const rows = useMemo(
    () => (run.data?.entities ?? []).filter((row) => row.dataflowId === dataflowId),
    [run.data, dataflowId],
  );
  const inFlight = run.data !== undefined && !isTerminalRun(run.data.status);
  usePollWhile(inFlight, RUN_POLL_INTERVAL_MS, () => {
    void run.refetch();
  });

  const headingId = `${panelId(runId)}-heading`;

  /** The tables: loading, a run no longer there, its tables, or a sentence saying it has none. */
  const renderTables = (): ReactElement => {
    if (run.isLoading) {
      return (
        <SkyText size={200} color="muted" block role="status">
          Loading the tables…
        </SkyText>
      );
    }
    if (run.isError || !run.data) {
      return (
        <SkyText size={200} color="muted" block testId={`${panelId(runId)}-unavailable`}>
          This run is no longer available.
        </SkyText>
      );
    }
    if (rows.length > 0) {
      return (
        <RunTablesGrid
          rows={rows}
          terminal={isTerminalRun(run.data.status)}
          noDestination={loadHadNoDestination(run.data.steps)}
          waitingLoads={loadsWaitingForSlot(run.data.steps)}
          sendingLoads={loadsSendingFiles(run.data.steps)}
          runId={runId}
          testId={`${panelId(runId)}-grid`}
        />
      );
    }
    return (
      <SkyText size={200} color="muted" block testId={`${panelId(runId)}-none`}>
        {isTerminalRun(run.data.status)
          ? "This run reported no table for this connection."
          : "No table has reported yet."}
      </SkyText>
    );
  };

  return (
    <SkyCard className="p-4" testId={panelId(runId)}>
      <section id={panelId(runId)} aria-labelledby={headingId}>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <SkyText as="h4" id={headingId} size={300} weight="semibold">
            Tables in run <span className="font-mono">{runId}</span>
          </SkyText>
          <SkyButton
            appearance="subtle"
            size="small"
            className="ml-auto"
            testId={`${panelId(runId)}-hide`}
            onClick={onHide}
          >
            Hide tables
          </SkyButton>
        </div>
        {renderTables()}
      </section>
    </SkyCard>
  );
}

function LoadingRows() {
  return (
    <SkySkeleton testId="connection-runs-loading">
      <div className="flex flex-col gap-3 p-2">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex items-center gap-4">
            <SkySkeletonItem width={90} borderRadius={999} />
            <SkySkeletonItem width={160} />
            <SkySkeletonItem width={220} />
            <SkySkeletonItem width={80} />
          </div>
        ))}
      </div>
    </SkySkeleton>
  );
}

function RunsLoadError({ error, onRetry }: { error: ApiClientError; onRetry: () => void }) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      testId="connection-runs-error"
      title="We could not load this connection's runs"
      message={failureMessage(error, "We could not load this connection's runs.")}
      actions={
        <SkyButton size="small" icon={RetryIcon} onClick={onRetry}>
          Try again
        </SkyButton>
      }
    />
  );
}

/** "No imports yet", with Import now for a reader who may start one. */
function NoImportsYet({
  name,
  importAction,
  importOffReason,
  onHoldFocus,
  onBlur,
}: {
  name: string;
  /** Present only for a reader with `runs.start`. */
  importAction: ConnectionImportAction | undefined;
  importOffReason: string | null;
  /** SkyCard passes no focus handlers through; the wrapper records focus in the card. */
  onHoldFocus: () => void;
  onBlur: (event: FocusEvent<HTMLElement>) => void;
}) {
  const unavailable = importOffReason !== null || !!importAction?.pending;
  return (
    <div onFocus={onHoldFocus} onBlur={onBlur}>
      <SkyCard className="max-w-xl p-6" testId="connection-runs-empty">
        <SkyText as="h4" size={300} weight="semibold" block>
          No imports yet.
        </SkyText>
        <SkyText as="p" size={300} color="muted" block className="mt-2">
          Each import of {name} is listed here, by hand or on a schedule.
        </SkyText>
        {importAction ? (
          <div className="mt-4 flex flex-col items-start gap-2">
            {/* aria-disabled with the click swallowed, as the header's ImportNowButton: a
                browser drops focus from a control that becomes natively disabled. */}
            <SkyButton
              appearance="primary"
              icon={PlayIcon}
              aria-disabled={unavailable ? true : undefined}
              aria-busy={importAction.pending ? true : undefined}
              aria-describedby={importOffReason ? "connection-runs-not-ready" : undefined}
              className={unavailable ? "cursor-not-allowed opacity-50" : undefined}
              testId="connection-runs-import-now"
              onClick={() => {
                if (!unavailable) importAction.onImport();
              }}
            >
              Import now
            </SkyButton>
            {importOffReason ? (
              <SkyText
                id="connection-runs-not-ready"
                size={200}
                color="muted"
                testId="connection-runs-not-ready"
              >
                {importOffReason}
              </SkyText>
            ) : null}
          </div>
        ) : null}
      </SkyCard>
    </div>
  );
}

/**
 * The same bar serves a failed next page and a failed background re-read; each says what
 * actually failed. The rows already shown stay.
 */
function MoreRunsError({ nextError, error }: { nextError: unknown; error: ApiClientError | null }) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      testId="connection-runs-more-error"
      title={
        nextError ? "We could not load more runs" : "We could not refresh this connection's runs"
      }
      message={failureMessage(
        nextError ?? error,
        nextError ? "We could not load more runs." : "We could not refresh this connection's runs.",
      )}
    />
  );
}

function noRunsYet({
  loading,
  rowCount,
  failed,
  firstPage,
}: {
  loading: boolean;
  rowCount: number;
  failed: boolean;
  firstPage: boolean;
}): boolean {
  return !loading && rowCount === 0 && !failed && firstPage;
}

/** `?run={runId}` when it is a run id; any other value is ignored, never echoed. */
function requestedRunOf(searchParams: URLSearchParams): string | null {
  const requestedRun = searchParams.get("run");
  return requestedRun && RUN_ID_REGEX.test(requestedRun) ? requestedRun : null;
}

/** The grid of this connection's runs, the pager, and the runs whose tables are open. */
function LoadedRuns({
  frameRef,
  layout,
  rows,
  nextError,
  error,
  range,
  total,
  pager,
  hasNext,
  onNext,
  nextPending,
  expanded,
  dataflowId,
  onHide,
}: {
  frameRef: RefObject<HTMLDivElement | null>;
  layout: { columns: SkyDataGridColumn<RunSummaryRow>[]; floor: number };
  rows: RunSummaryRow[];
  nextError: unknown;
  error: ApiClientError | null;
  range: { start: number; end: number };
  total: number | null;
  pager: ReturnType<typeof useCursorPager>;
  hasNext: boolean;
  onNext: () => void;
  nextPending: boolean;
  expanded: string[];
  dataflowId: string;
  onHide: (runId: string) => void;
}) {
  return (
    <>
      {/* The grid's header cells never wrap, and the row counts' two-word headers do not fit
        one line each: "Rows extracted" takes two lines instead of running into its
        neighbour (review r1 S2). Below its floor the grid scrolls sideways under a pinned
        Actions column (round 3); the floor fits the pane at 1280 px (gridsfit). */}
      <div
        ref={frameRef}
        className="[&_th]:whitespace-normal"
        style={{ "--connection-runs-floor": `${layout.floor}px` } as CSSProperties}
        data-testid="connection-runs-grid-frame"
      >
        <SkyDataGrid<RunSummaryRow>
          data={rows}
          columns={layout.columns}
          getRowId={(row) => row.runId}
          bordered
          className="grid-pinned-last [&_table]:min-w-(--connection-runs-floor)"
          testId="connection-runs-grid"
        />
      </div>

      {nextError || error ? <MoreRunsError nextError={nextError} error={error} /> : null}

      <CursorPager
        testId="connection-runs-pager"
        label="Imports of this connection, pages"
        noun="imports"
        start={range.start}
        end={range.end}
        total={total}
        pageSize={pager.pageSize}
        pageSizes={pager.pageSizes}
        onPageSizeChange={pager.setPageSize}
        hasPrevious={!pager.isFirstPage}
        onPrevious={pager.previous}
        hasNext={hasNext}
        onNext={onNext}
        nextPending={nextPending}
      />

      {rows
        .filter((row) => expanded.includes(row.runId))
        .map((row) => (
          <ExpandedRunTables
            key={row.runId}
            runId={row.runId}
            dataflowId={dataflowId}
            onHide={() => onHide(row.runId)}
          />
        ))}
    </>
  );
}

export function ConnectionRunsTab({ dataflow, importAction, need }: ConnectionRunsTabProps) {
  const { can } = usePermissions();
  const canStartRun = can("runs.start");
  // The header's own answer: disabled, setup incomplete, running, then the destination (#1080).
  const importOffReason = importOffReasonOf(
    dataflow.state,
    importAction?.active === true,
    destinationBlockReason(useDestinationReadiness()),
    need,
  );
  const name = dataflow.displayName;

  const filters = useMemo(() => ({ dataflowId: dataflow.id }), [dataflow.id]);
  const pager = useCursorPager({
    pageSizes: RUNS_PAGE_SIZES,
    defaultPageSize: RUNS_DEFAULT_PAGE_SIZE,
    resetKey: dataflow.id,
  });
  // A new page size keeps this connection's rows on screen while it loads (and the pager with
  // its focused control); another connection's rows are never kept.
  const runs = useRunsPage(filters, pager.cursor, pager.pageSize, {
    keepPreviousRows: "sameFilters",
  });
  const rows = useMemo(() => runs.data?.rows ?? [], [runs.data]);
  const total = runs.data?.total ?? null;
  const nextCursor = runs.data?.nextCursor ?? null;

  // Round 4 (4b), as on the Runs list: a later page never shifts; the runs that arrived above it
  // since the reader left the first page are counted instead.
  const next = useNextPage({
    pager,
    pageKey: `${dataflow.id}|${pager.pageSize}`,
    total,
    nextCursor,
    fetchPage: runs.fetchPage,
  });
  const newer = next.newer;
  const range = pagerRange({
    pageIndex: pager.pageIndex,
    pageSize: pager.pageSize,
    rowCount: rows.length,
    newer,
  });
  const nextError = next.error;
  const goNext = next.goNext;
  // The grid mounts once the first page is in; the pinned edge follows it from then on.
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(!runs.isLoading && rows.length > 0);

  // A run still queued or running is re-read until it is over, so the tab never sits on
  // "Running" after the run finished. The list read is not audited; the poll stops with the run.
  // Only the first page decides, and only the first page is re-read: a new or live run is at the
  // top (newest first), and a later page is a place the reader chose (round 4, 4b).
  const anyActive = pager.isFirstPage && rows.some((row) => !isTerminalRun(row.status));
  usePollWhile(anyActive, RUN_POLL_INTERVAL_MS, () => {
    void runs.refetch();
  });

  // Session 11 (P3): `?run={runId}` (a schedule's History "Steps") opens that run's row, and the
  // open panel is brought into view once the run is listed, with focus on its toggle so keyboard
  // and screen-reader users start at that run, not at the top of the page. A value that is not a
  // run id is ignored, never echoed into an id or a request.
  const [searchParams] = useSearchParams();
  const initialRun = requestedRunOf(searchParams);
  const [expanded, setExpanded] = useState<string[]>(() => (initialRun ? [initialRun] : []));
  const toggles = useRef(new Map<string, HTMLElement>());
  const revealedInitialRun = useRef(false);
  const initialRunListed = initialRun !== null && rows.some((row) => row.runId === initialRun);
  useEffect(() => {
    if (!initialRun || !initialRunListed || revealedInitialRun.current) return;
    revealedInitialRun.current = true;
    requestAnimationFrame(() => {
      toggles.current.get(initialRun)?.focus({ preventScroll: true });
      document.getElementById(panelId(initialRun))?.scrollIntoView?.({ block: "start" });
    });
  }, [initialRun, initialRunListed]);

  const toggle = (runId: string) => {
    if (expanded.includes(runId)) {
      setExpanded((previous) => previous.filter((id) => id !== runId));
      return;
    }
    setExpanded((previous) => (previous.includes(runId) ? previous : [...previous, runId]));
    // After the panel mounts, bring it into view: it opens under the grid.
    requestAnimationFrame(() => {
      document.getElementById(panelId(runId))?.scrollIntoView?.({ block: "nearest" });
    });
  };

  const hide = (runId: string) => {
    setExpanded((previous) => previous.filter((id) => id !== runId));
    // The panel's own button is about to go; focus returns to the row that opened it.
    toggles.current.get(runId)?.focus();
  };

  const columns: SkyDataGridColumn<RunSummaryRow>[] = [
    {
      key: "status",
      header: "Status",
      width: `${STATUS_WIDTH}px`,
      renderCell: (_value, row) => {
        const presentation = runStatusPresentation(ownRunStatus(row));
        return (
          <StatusCell
            badge={
              <SkyBadge
                appearance="tint"
                color={presentation.color}
                testId={`connection-run-status-${row.runId}`}
              >
                {presentation.label}
              </SkyBadge>
            }
          />
        );
      },
    },
    {
      // gridsfit: the start over this connection's own duration (`StartedCell`); the old
      // Duration cell's test id is on the value.
      key: "createdAt",
      header: "Started",
      width: `${STARTED_WIDTH}px`,
      renderCell: (_value, row) =>
        // #986: a queued run has not started, so the creation time is not a start time.
        isNotStarted(row) ? (
          <SkyText size={200} color="muted" block testId={`run-not-started-${row.runId}`}>
            {NOT_STARTED_LABEL}
          </SkyText>
        ) : (
          <StartedCell
            at={row.startedAt ?? row.createdAt}
            durationSeconds={row.connection?.durationSeconds ?? null}
            durationTestId={`connection-run-duration-${row.runId}`}
            runningSince={row.status === "running" ? connectionRunningSince(row) : null}
          />
        ),
    },
    {
      // Session 11 (D3): the same cell as the Runs list, so one run reads the same on both;
      // gridsfit: the trigger is its second line (there is no Trigger column).
      key: "startedBy",
      header: "Started by",
      width: "10%",
      renderCell: (_value, row) => (
        <StartedByCell row={row} trigger={STARTED_BY_KIND_LABELS[row.kind] ?? row.kind} />
      ),
    },
    {
      key: "steps",
      header: "Steps",
      width: "11%",
      renderCell: (_value, row) => <StepsCell row={row} />,
    },
    {
      key: "tables",
      header: "Tables",
      width: "8%",
      align: "right",
      renderCell: (_value, row) => (
        <NumberCell
          value={formatCount(row.connection?.totals?.tables)}
          testId={`connection-run-tables-count-${row.runId}`}
        />
      ),
    },
    {
      key: "rowsExtracted",
      header: "Rows extracted",
      width: "11%",
      align: "right",
      renderCell: (_value, row) => (
        <NumberCell
          value={rowsExtractedText(row.connection?.totals)}
          testId={`connection-run-extracted-${row.runId}`}
        />
      ),
    },
    {
      key: "rowsLoaded",
      header: "Rows loaded",
      width: "9%",
      align: "right",
      renderCell: (_value, row) => (
        <NumberCell
          value={formatCount(row.connection?.totals?.rowsLoaded)}
          testId={`connection-run-loaded-${row.runId}`}
        />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: `${ACTIONS_WIDTH}px`,
      align: "right",
      renderCell: (_value, row) => {
        const open = expanded.includes(row.runId);
        return (
          <div className="flex items-center justify-end gap-2">
            <SkyButton
              ref={(element: HTMLButtonElement | null) => {
                if (element) toggles.current.set(row.runId, element);
                else toggles.current.delete(row.runId);
              }}
              appearance="subtle"
              size="small"
              aria-expanded={open}
              aria-controls={open ? panelId(row.runId) : undefined}
              aria-label={`${open ? "Hide" : "Show"} tables for run ${row.runId}`}
              testId={`connection-run-expand-${row.runId}`}
              onClick={() => toggle(row.runId)}
            >
              Tables
            </SkyButton>
            <Link
              href={absolutePath(`/runs/${row.runId}`)}
              aria-label={`View run ${row.runId}`}
              className="underline-offset-2 hover:underline"
              data-testid={`connection-run-view-${row.runId}`}
            >
              <SkyText size={200} color="accent">
                View
              </SkyText>
            </Link>
          </div>
        );
      },
    },
  ];

  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });

  const error = runs.error as ApiClientError | null;
  const seeAll = absolutePath(`/runs?dataflowId=${encodeURIComponent(dataflow.id)}`);
  // Only a first page that read and holds no run is the empty state; a failed read is not.
  const showEmpty = noRunsYet({
    loading: runs.isLoading,
    rowCount: rows.length,
    failed: Boolean(error),
    firstPage: pager.isFirstPage,
  });

  // Focus never falls to <body> (ux-s10.md §2). The empty card's Import now holds focus while
  // the import is asked for; the card unmounts when the new run is listed, so focus moves to
  // this tab's heading, over the grid that replaced it, unless it has already gone elsewhere.
  const heading = useRef<HTMLElement>(null);
  const emptyHeldFocus = useRef(false);
  useLayoutEffect(() => {
    if (showEmpty || !emptyHeldFocus.current) return;
    emptyHeldFocus.current = false;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    heading.current?.focus();
  }, [showEmpty]);
  const onEmptyBlur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    emptyHeldFocus.current = false;
  };

  /** The tab's body: loading, the load failure, the empty state, or the runs. */
  const renderBody = (): ReactElement => {
    if (runs.isLoading) return <LoadingRows />;
    if (error && rows.length === 0) {
      return <RunsLoadError error={error} onRetry={() => void runs.refetch()} />;
    }
    if (showEmpty) {
      return (
        <NoImportsYet
          name={name}
          importAction={canStartRun ? importAction : undefined}
          importOffReason={importOffReason}
          onHoldFocus={() => {
            emptyHeldFocus.current = true;
          }}
          onBlur={onEmptyBlur}
        />
      );
    }
    return (
      <LoadedRuns
        frameRef={pinnedEdge}
        layout={layout}
        rows={rows}
        nextError={nextError}
        error={error}
        range={range}
        total={total}
        pager={pager}
        hasNext={nextCursor !== null}
        onNext={() => void goNext()}
        nextPending={next.pending}
        expanded={expanded}
        dataflowId={dataflow.id}
        onHide={hide}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4" data-testid="connection-runs-tab">
      <div className="flex flex-wrap items-center gap-3">
        <SkyText
          ref={heading}
          as="h3"
          size={300}
          weight="semibold"
          tabIndex={-1}
          className="outline-none"
          testId="connection-runs-heading"
        >
          Imports of this connection
        </SkyText>
        <SkyText size={200} color="muted">
          Rows, tables and durations are this connection&apos;s share of each run.
        </SkyText>
        <Link
          href={seeAll}
          className="ml-auto underline-offset-2 hover:underline"
          data-testid="connection-runs-see-all"
        >
          <SkyText size={200} weight="semibold" color="accent">
            See all in Runs
          </SkyText>
        </Link>
      </div>

      {newer > 0 ? (
        <div>
          <NewerRowsNotice
            count={newer}
            one="import"
            other="imports"
            onBack={() => {
              void runs.invalidatePage(null);
              pager.first();
            }}
            testId="connection-runs-newer"
          />
        </div>
      ) : null}

      {renderBody()}
    </div>
  );
}
